import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector, Footer } from '../components/Navigation';
import { TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { HeroSection } from '../components/landing/HeroSection';
import { CoursesSection } from '../components/landing/CoursesSection';
import { TestimonialsSection } from '../components/landing/TestimonialsSection';
import { NewsletterSection } from '../components/landing/NewsletterSection';

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [tickerData, setTickerData] = useState([]);

  useEffect(() => {
    const fetchTicker = async () => {
      try {
        const response = await api.get('/api/market/ticker');
        setTickerData(response.data.data);
      } catch (error) {
        // Ticker fetch failed
      }
    };
    fetchTicker();
    const interval = setInterval(fetchTicker, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Navigation */}
      <nav className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#007AFF] rounded-sm flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              COSSY NETWORK
            </span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#courses" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-courses">
              {t('nav.courses')}
            </a>
            <Link to="/analysis" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-analysis">
              {t('nav.analysis')}
            </Link>
            <a href="#testimonials" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-testimonials">
              {t('nav.testimonials')}
            </a>
          </div>
          
          <div className="flex items-center gap-4">
            <LanguageSelector />
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="btn-primary" data-testid="dashboard-btn">{t('nav.dashboard')}</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="login-btn">
                    {t('nav.signIn')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary" data-testid="register-btn">{t('nav.getStarted')}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Live Ticker */}
      {tickerData.length > 0 && (
        <div className="fixed top-[72px] left-0 right-0 z-40 bg-[#141417] border-b border-[#27272A] py-2 overflow-hidden">
          <div className="ticker-marquee flex items-center gap-8 whitespace-nowrap">
            {[...tickerData, ...tickerData].map((item, index) => (
              <div key={`${item.symbol}-${index}`} className="flex items-center gap-2 px-4">
                <span className="font-mono text-white font-medium">{item.symbol}</span>
                <span className="font-mono text-white">${item.price.toFixed(2)}</span>
                <span className={`font-mono ${item.change >= 0 ? 'ticker-up' : 'ticker-down'}`}>
                  {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)} ({item.change_percent.toFixed(2)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <HeroSection />
      <CoursesSection />
      <TestimonialsSection />
      <NewsletterSection />
      <Footer />
    </div>
  );
}
